/**
 * The board grid (presentational): categories × clue values, marking consumed
 * cells unavailable across all clients in real time (FR-3.1). Reused by the host
 * (cells are clickable to select — FR-3.2) and the presentation view (read-only).
 * The player view shows a compact read-only board too.
 */

import type { Board as BoardModel, Clue, PackSnapshot } from '@jeopardy/shared';

export interface BoardProps {
  board: BoardModel;
  cluesById: PackSnapshot['cluesById'];
  /** When provided, cells are selectable and call this with the clue id (host). */
  onSelectClue?: (clueId: string) => void;
}

export function Board({ board, cluesById, onSelectClue }: BoardProps): React.JSX.Element {
  const cats = board.categories;
  const rows = Math.max(0, ...cats.map((c) => c.clueIds.length));
  return (
    <div className="board-grid" style={{ ['--cats' as string]: cats.length }}>
      {cats.map((cat) => (
        <div key={cat.id} className="board-category">
          {cat.name}
        </div>
      ))}
      {Array.from({ length: rows }).map((_row, rowIndex) =>
        cats.map((cat) => {
          const clueId = cat.clueIds[rowIndex];
          const clue: Clue | undefined = clueId ? cluesById[clueId] : undefined;
          if (!clue) {
            return (
              <div
                key={`${cat.id}-${rowIndex}`}
                className="board-cell board-cell--consumed"
              />
            );
          }
          const consumed = board.consumedClueIds.includes(clue.id);
          const selectable = !consumed && onSelectClue !== undefined;
          return (
            <button
              key={clue.id}
              type="button"
              className={
                'board-cell' +
                (consumed ? ' board-cell--consumed' : '') +
                (selectable ? ' board-cell--selectable' : '')
              }
              disabled={!selectable}
              aria-label={`${cat.name} for $${clue.value}${consumed ? ' (used)' : ''}`}
              data-testid={`cell-${clue.id}`}
              onClick={selectable ? () => onSelectClue!(clue.id) : undefined}
            >
              {consumed ? '' : `$${clue.value}`}
            </button>
          );
        }),
      )}
    </div>
  );
}
